import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Heart, Bookmark, Flag, Trash2, Eye } from "lucide-react";
import { AccountBadge } from "../components/Badges";
import Seo from "../components/Seo";

const EMOJIS = [["thumbs_up","👍"],["heart","❤️"],["fire","🔥"]];

function CommentItem({ c, onReply, onDelete, onReact, onFlag, canDelete }) {
  return (
    <div data-testid={`comment-${c.id}`}>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-neutral-200 overflow-hidden">{c.author_avatar && <img src={c.author_avatar} alt="" className="w-full h-full object-cover"/>}</div>
        <Link to={`/artists/${c.author_username}`} className="text-sm font-medium hover:underline">{c.author_name}</Link>
        <AccountBadge type={c.author_account_type}/>
        <span className="text-xs text-black/40">· {new Date(c.created_at).toLocaleDateString()}</span>
      </div>
      <div className="text-sm text-black/80 mt-2 whitespace-pre-wrap">{c.body}</div>
      <div className="flex items-center gap-3 mt-2 text-xs text-black/60">
        {EMOJIS.map(([key, e]) => (
          <button key={key} onClick={()=>onReact(c.id, key)} className="hover:text-black" data-testid={`react-${key}-${c.id}`}>{e} {(c.reactions?.[key]||[]).length || ""}</button>
        ))}
        {onReply && <button onClick={onReply} className="underline underline-offset-4" data-testid={`reply-${c.id}`}>Reply</button>}
        <button onClick={()=>onFlag(c.id)} className="ml-auto" data-testid={`flag-comment-${c.id}`}>Report</button>
        {canDelete && <button onClick={()=>onDelete(c.id)} className="text-red-700" data-testid={`del-comment-${c.id}`}>Delete</button>}
      </div>
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [authorPosts, setAuthorPosts] = useState([]);

  const load = async () => {
    const r = await api.get(`/blog/posts/${slug}`);
    setPost(r.data);
    const c = await api.get(`/blog/posts/${r.data.id}/comments`);
    setComments(c.data);
    const a = await api.get("/blog/posts");
    setAuthorPosts(a.data.filter((p) => p.author_id === r.data.author_id && p.slug !== r.data.slug).slice(0, 3));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  if (post === null) return <div className="container-x py-20 text-black/50">Loading...</div>;

  const like = async () => { if (!user) return toast.error("Sign in"); await api.post(`/blog/posts/${post.id}/like`); load(); };
  const save = async () => { if (!user) return toast.error("Sign in"); await api.post(`/blog/posts/${post.id}/save`); toast.success("Saved"); load(); };
  const flag = async () => { if (!user) return toast.error("Sign in"); await api.post("/flags", { target_type: "blog_post", target_id: post.id, reason: "" }); toast.success("Reported."); };
  const del = async () => { if (window.confirm("Delete this post?")) { await api.delete(`/blog/posts/${post.id}`); toast.success("Deleted"); nav("/blog"); } };
  const submitComment = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to comment");
    await api.post(`/blog/posts/${post.id}/comments`, { body, parent_id: replyTo });
    setBody(""); setReplyTo(null); load();
  };
  const delComment = async (cid) => { await api.delete(`/blog/comments/${cid}`); load(); };
  const react = async (cid, emoji) => { if (!user) return toast.error("Sign in"); await api.post(`/blog/comments/${cid}/react`, { emoji }); load(); };
  const flagComment = async (cid) => { if (!user) return toast.error("Sign in"); await api.post("/flags", { target_type: "blog_comment", target_id: cid, reason: "" }); toast.success("Reported."); };

  const isAuthor = user && post.author_id === user.id;
  const isAdmin = user?.role === "admin";

  const topLevel = comments.filter((c) => !c.parent_id);
  const childrenOf = (id) => comments.filter((c) => c.parent_id === id);

  return (
    <div className="container-x py-12 max-w-3xl" data-testid="blog-post-page">
      <Seo title={post.title} type="article" image={post.cover_image_url} description={(post.body || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 160) || undefined}/>
      <Link to="/blog" className="text-sm text-black/60 underline underline-offset-4">← All stories</Link>
      <div className="mt-6 flex items-center gap-2 flex-wrap">
        <span className="badge badge-neutral">{post.category}</span>
        {post.author_account_type === "artist" && <span className="badge badge-flash">Artist</span>}
        {post.featured && <span className="badge badge-open">Featured</span>}
      </div>
      <h1 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">{post.title}</h1>
      <div className="flex items-center gap-3 mt-6 text-sm text-black/60">
        <div className="w-9 h-9 rounded-full bg-neutral-200 overflow-hidden">{post.author_avatar && <img src={post.author_avatar} alt="" className="w-full h-full object-cover"/>}</div>
        <div>
          <Link to={`/artists/${post.author_username}`} className="text-black font-medium hover:underline">{post.author_name}</Link>
          <div className="text-xs">{post.published_at ? new Date(post.published_at).toLocaleDateString() : ""} · {post.read_time} min read · <Eye size={11} className="inline"/> {post.views}</div>
        </div>
      </div>
      {post.cover_image_url && <img src={post.cover_image_url} alt={post.title} className="mt-8 rounded-[12px] w-full max-h-[440px] object-cover border-[0.5px] border-black/10"/>}
      <div className="article-body mt-8" dangerouslySetInnerHTML={{ __html: post.body }}/>
      <div className="border-t-[0.5px] border-black/10 mt-10 pt-6 flex items-center gap-4 text-sm">
        <button onClick={like} className="flex items-center gap-1.5 hover:text-black text-black/70" data-testid="blog-like"><Heart size={16} fill={post.liked?"#000":"none"}/> {post.like_count}</button>
        <button onClick={save} className="flex items-center gap-1.5 hover:text-black text-black/70" data-testid="blog-save"><Bookmark size={16} fill={post.saved?"#000":"none"}/> Save</button>
        <button onClick={flag} className="flex items-center gap-1.5 hover:text-black text-black/70 ml-auto" data-testid="blog-flag"><Flag size={14}/> Report</button>
        {(isAuthor || isAdmin) && (
          <>
            <Link to={`/blog/${post.slug}/edit`} className="text-black/70 hover:text-black" data-testid="blog-edit">Edit</Link>
            <button onClick={del} className="text-red-700 hover:text-red-900 flex items-center gap-1" data-testid="blog-delete"><Trash2 size={14}/> Delete</button>
          </>
        )}
      </div>

      <div className="mt-12">
        <h3 className="font-serif text-2xl mb-4">Comments ({comments.length})</h3>
        {user ? (
          <form onSubmit={submitComment} className="card mb-6" data-testid="comment-form">
            {replyTo && <div className="text-xs text-black/60 mb-2">Replying · <button type="button" onClick={()=>setReplyTo(null)} className="underline">cancel</button></div>}
            <textarea rows={3} required value={body} onChange={(e)=>setBody(e.target.value)} placeholder="Add to the discussion..." className="input-field" data-testid="comment-body"/>
            <button className="btn-filled mt-3" data-testid="comment-submit">Post comment</button>
          </form>
        ) : <p className="text-sm text-black/60 mb-6"><Link to="/sign-in" className="underline">Sign in</Link> to comment.</p>}
        <div className="space-y-6">
          {topLevel.map((c) => (
            <div key={c.id}>
              <CommentItem c={c} onReply={() => setReplyTo(c.id)} onDelete={delComment} onReact={react} onFlag={flagComment} canDelete={user && (c.author_id === user.id || isAuthor || isAdmin)}/>
              <div className="pl-6 mt-3 space-y-3 border-l-[0.5px] border-black/10">
                {childrenOf(c.id).map((r) => (
                  <CommentItem key={r.id} c={r} onReact={react} onDelete={delComment} onFlag={flagComment} canDelete={user && (r.author_id === user.id || isAuthor || isAdmin)}/>
                ))}
              </div>
            </div>
          ))}
          {comments.length === 0 && <p className="text-black/50">No comments yet.</p>}
        </div>
      </div>

      {authorPosts.length > 0 && (
        <div className="mt-16 border-t-[0.5px] border-black/10 pt-8">
          <h3 className="font-serif text-2xl mb-4">More by {post.author_name}</h3>
          <div className="grid md:grid-cols-3 gap-4">
            {authorPosts.map((p) => (
              <Link to={`/blog/${p.slug}`} key={p.id} className="card hover:-translate-y-0.5 transition-transform">
                <span className="badge badge-neutral mb-2">{p.category}</span>
                <h4 className="font-serif text-lg">{p.title}</h4>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
